import { CellValueChangedEvent } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { locationColumnDef, mapLocationForm } from 'contacts/components';
import { ContactGroupType } from 'contacts/enums';
import { useContactGroupActions, useGetContactGroupAddresses } from 'contacts/hooks';
import { IContactGroup } from 'contacts/types';
import { SlidingPanelContent } from 'core/components';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';

import { ContactGroupLocationListTable } from './ContactGroupLocationListTable';

interface IProps {
  contactGroup: IContactGroup;
  contactGroupType: ContactGroupType;
}

export const ContactGroupLocationList = ({ contactGroup, contactGroupType }: IProps) => {
  const { t } = useTranslation();

  const { records, isLoading, loadingPercentage, currentPage } = useGetContactGroupAddresses({
    filters: {
      contactGroupId: contactGroup.id,
    },
  });

  const { upsertContactGroupAddress } = useContactGroupActions(NotificationPosition.TOP);

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const columnDefs = useMemo(
    () => locationColumnDef({ contactGroupType, isGlobal: false }),
    [contactGroupType]
  );

  const onCellValueChanged = useCallback(
    async (_, { colDef: { field }, data, newValue }: CellValueChangedEvent) => {
      const { success, error, contactGroupAddress } = await upsertContactGroupAddress(
        mapLocationForm(
          {
            contactGroup: data.contactGroup,
            addressId: data.addressId,
            [field]: newValue,
          },
          contactGroupType
        )
      );

      if (!success) {
        throw new Error(error);
      }

      return { updatedRow: contactGroupAddress };
    },
    [contactGroupType, upsertContactGroupAddress]
  );

  const getLayoutType = useCallback(() => {
    switch (contactGroupType) {
      case ContactGroupType.VENDOR:
        return TableLayoutType.VENDOR_CONTACT_GROUP_LOCATION;
      case ContactGroupType.CUSTOMER:
      default:
        return TableLayoutType.CUSTOMER_CONTACT_GROUP_LOCATION;
    }
  }, [contactGroupType]);

  return (
    <AgGridClientTableProvider
      tableId="contactGroupLocationList"
      entityType="Location"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      onCellValueChanged={onCellValueChanged}
      data={records as AgGridDataRow[]}
      isDataLoading={isLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      layoutType={getLayoutType()}
      hasLayoutActions
    >
      <SlidingPanelContent title={t('titleLocations')}>
        <ContactGroupLocationListTable
          contactGroup={contactGroup}
          contactGroupType={contactGroupType}
        />
      </SlidingPanelContent>
    </AgGridClientTableProvider>
  );
};
